import { createFileRoute, Link, useSearch } from '@tanstack/react-router'
import { Button } from "@/src/components/ui/button"
import { Input } from "@/src/components/ui/input"
import { Alert, AlertDescription } from "@/src/components/ui/alert"
import { useState, useEffect } from "react"
import { SpinnerOverlay } from "@/src/components/SpinnerOverlay"
import { useAuth } from "@/src/components/AuthContextProvider"
import { authClient } from "@/src/lib/auth-client"
import { useTranslation } from "@/src/i18n/client"
import { z } from 'zod'

const loginSearchSchema = z.object({
  error: z.string().optional(),
  error_description: z.string().optional(),
  callbackUrl: z.string().optional(),
})

export const Route = createFileRoute('/_auth/login/email')({
  component: EmailLoginPage,
  validateSearch: loginSearchSchema,
})

function getHumanReadableError(error?: string, description?: string): string | null {
  if (!error && !description) return null
  
  // Map common OAuth error codes to human-readable messages
  const errorMessages: Record<string, string> = {
    'auth_cancelled': 'Authentication was cancelled',
    'access_denied': 'Access was denied',
    'server_error': 'Server error occurred during authentication',
    'temporarily_unavailable': 'Authentication service is temporarily unavailable',
    'invalid_request': 'Invalid authentication request',
    'unauthorized_client': 'Unauthorized client',
  }
  
  if (error && errorMessages[error]) {
    return description ? `${errorMessages[error]}: ${description}` : errorMessages[error]
  }
  
  return description || error || 'Authentication error occurred'
}

function EmailLoginPage() {
  const { t } = useTranslation('auth')
  const search = useSearch({ from: '/_auth/login/email' })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState({ email: '', password: '' })
  const { status } = useAuth()
  
  // Get error from URL params
  const urlError = getHumanReadableError(search.error, search.error_description)

  useEffect(() => {
    if (status === "authenticated") {
      // Check for callbackUrl in search params
      const callbackUrl = search.callbackUrl || "/"
      window.location.assign(callbackUrl)
    }
  }, [status, search.callbackUrl])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleEmailPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)
    
    try {
      const result = await authClient.signIn.email({
        email: form.email,
        password: form.password,
      })
      
      if (result.error) {
        setError(result.error.message || t('emailLogin.errorSignIn'))
      } else {
        // Force page reload to refresh auth context
        const callbackUrl = search.callbackUrl || "/"
        window.location.href = callbackUrl
      }
    } catch (err) {
      setError(t('emailLogin.errorCredentials'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="relative">
      {isLoading && <SpinnerOverlay text={t('emailLogin.signingIn')} />}
      
      {/* Back button */}
      <Link 
        to="/login" 
        className="inline-flex items-center gap-2 mb-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <svg 
          className="w-4 h-4" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M15 19l-7-7 7-7" 
          />
        </svg>
        {t('emailLogin.backToOptions')}
      </Link>

      <h2 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">
        {t('emailLogin.title')}
      </h2>
      
      {(error || urlError) && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            {error || urlError}
          </AlertDescription>
        </Alert>
      )}

      {/* Email/Password form */}
      <form className="space-y-6" onSubmit={handleEmailPasswordSubmit}>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground">
            {t('emailLogin.emailAddress')}
          </label>
          <Input 
            id="email" 
            name="email" 
            type="email" 
            autoComplete="email" 
            required 
            value={form.email}
            onChange={handleChange}
            className="h-10 mt-1 block w-full rounded-md border border-input bg-background text-foreground shadow-sm focus:border-primary focus:ring-primary" 
          />
        </div>
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-foreground">
            {t('emailLogin.password')}
          </label>
          <Input 
            id="password" 
            name="password" 
            type="password" 
            autoComplete="current-password" 
            required 
            value={form.password}
            onChange={handleChange}
            className="h-10 mt-1 block w-full rounded-md border border-input bg-background text-foreground shadow-sm focus:border-primary focus:ring-primary" 
          />
          <div className="mt-2 text-right">
            <Link 
              to="/forgot-password" 
              className="text-sm text-primary hover:text-primary/80"
            >
              {t('emailLogin.forgotPassword')}
            </Link>
          </div>
        </div>
        <div>
          <Button 
            type="submit" 
            disabled={isLoading}
            className="flex w-full justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            {t('emailLogin.signInButton')}
          </Button>
        </div>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        {t('emailLogin.noAccount')}{' '}
        <Link to="/register" className="font-medium text-primary hover:text-primary/80">
          {t('emailLogin.createAccount')}
        </Link>
      </p>
    </div>
  )
}
